<template>
  <div>
    <div class="contact-form-wrapper">
      <h2 class="form-title">{{t('contactForm.title')}}</h2>
      <form @submit.prevent="submitForm" class="contact-form">
        <div class="form-group">
          <input
            v-model="form.name"
            type="text"
            id="name"
            required
            class="form-input"
            :placeholder="t('contactForm.namePlaceholder')"
            :class="{ 'error': errors.name }"
            @blur="validateName"
          />
          <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
        </div>

        <div class="form-group">
          <input
            v-model="form.email"
            type="email"
            id="email"
            required
            class="form-input"
            :placeholder="t('contactForm.emailPlaceholder')"
            :class="{ 'error': errors.email }"
            @blur="validateEmail"
          />
          <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <input
            v-model="form.subject"
            type="text"
            id="subject"
            required
            class="form-input"
            :placeholder="t('contactForm.subjectPlaceholder')"
            :class="{ 'error': errors.subject }"
            @blur="validateSubject"
          />
          <span v-if="errors.subject" class="error-message">{{ errors.subject }}</span>
        </div>

        <div class="form-group">
          <input
            v-model="form.phone"
            type="text"
            id="phone"
            required
            class="form-input"
            :placeholder="t('contactForm.phonePlaceholder')"
            :class="{ 'error': errors.phone }"
            @blur="validatePhone"
          />
          <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
        </div>
        <div class="form-group">
          <input
            v-model="form.country"
            type="text"
            id="country"
            required
            class="form-input"
            :placeholder="t('contactForm.countryPlaceholder')"
            :class="{ 'error': errors.country }"
            @blur="validateCountry"
          />
          <span v-if="errors.country" class="error-message">{{ errors.country }}</span>
        </div>
        <!-- SECTION SERVICES -->
        <div class="form-group">
          <label class="services-label">{{t('contactForm.servicesLabel')}}</label>
          <div class="services-options">
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.onePage')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.onePage')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.showcase')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.showcase')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.ecommerce')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.ecommerce')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.mobile')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.mobile')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.branding')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.branding')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.custom')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.custom')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.motion')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.motion')}}
            </label>
            <label class="checkbox-container">
              <input type="checkbox" :value="t('contactForm.other')" v-model="form.services" />
              <span class="checkmark"></span>
              {{t('contactForm.other')}}
            </label>
          </div>
          <span v-if="errors.services" class="error-message">{{ errors.services }}</span>
        </div>
        <!-- FIN SECTION SERVICES -->

        <div class="form-group">
          <select
            v-model="form.budget"
            id="budget"
            required
            class="form-input form-select"
            :class="{ 'error': errors.budget }"
            @blur="validateBudget"
          >
            <option value="" disabled selected>{{t('contactForm.budgetPlaceholder')}}</option>
            <option value="0-1k">{{t('contactForm.budget1')}}</option>
            <option value="1k-5k">{{t('contactForm.budget2')}}</option>
            <option value="5k-15k">{{t('contactForm.budget3')}}</option>
            <option value="15k-30k">{{t('contactForm.budget4')}}</option>
            <option value="30k+">{{t('contactForm.budget5')}}</option>
          </select>
          <span v-if="errors.budget" class="error-message">{{ errors.budget }}</span>
        </div>

        <div class="form-group">
          <textarea
            v-model="form.message"
            id="message"
            required
            rows="5"
            class="form-input form-textarea"
            :placeholder="t('contactForm.messagePlaceholder')"
            :class="{ 'error': errors.message }"
            @blur="validateMessage"
          ></textarea>
          <span v-if="errors.message" class="error-message">{{ errors.message }}</span>
        </div>

        <button
          type="submit"
          class="submit-button"
          :disabled="isSubmitting"
          :class="{ 'submitting': isSubmitting }"
        >
          <span v-if="!isSubmitting">{{t('contactForm.send')}}</span>
          <span v-else class="loading-spinner">
            <div class="spinner"></div>
            {{t('contactForm.sending')}}
          </span>
        </button>

        <div v-if="submitStatus === 'success'" class="success-message">
          {{t('contactForm.success')}}
        </div>
        <div v-if="submitStatus === 'error'" class="error-message">
          {{t('contactForm.error')}}
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const form = reactive({
  name: '',
  email: '',
  subject: '',
  phone: '',
  country: '',
  message: '',
  budget: '',
  services: []
})

const errors = reactive({})
const isSubmitting = ref(false)
const submitStatus = ref('idle')

const validateName = () => {
  if (!form.name.trim()) {
    errors.name = t('contactForm.errorNameRequired')
  } else if (form.name.length < 2) {
    errors.name = t('contactForm.errorNameLength')
  } else {
    delete errors.name
  }
}

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!form.email.trim()) {
    errors.email = t('contactForm.errorEmailRequired')
  } else if (!emailRegex.test(form.email)) {
    errors.email = t('contactForm.errorEmailFormat')
  } else {
    delete errors.email
  }
}

const validateSubject = () => {
  if (!form.subject.trim()) {
    errors.subject = t('contactForm.errorSubjectRequired')
  } else if (form.subject.length < 3) {
    errors.subject = t('contactForm.errorSubjectLength')
  } else {
    delete errors.subject
  }
}

const validateServices = () => {
  if (!form.services.length) {
    errors.services = t('contactForm.errorServicesRequired')
  } else {
    delete errors.services
  }
}

const validateBudget = () => {
  if (!form.budget) {
    errors.budget = t('contactForm.errorBudgetRequired')
  } else {
    delete errors.budget
  }
}

const validateMessage = () => {
  if (!form.message.trim()) {
    errors.message = t('contactForm.errorMessageRequired')
  } else if (form.message.length < 10) {
    errors.message = t('contactForm.errorMessageLength')
  } else {
    delete errors.message
  }
}

const validatePhone = () => {
  if (!form.phone.trim()) {
    errors.phone = t('contactForm.errorPhoneRequired')
  } else {
    delete errors.phone
  }
}
const validateCountry = () => {
  if (!form.country.trim()) {
    errors.country = t('contactForm.errorCountryRequired')
  } else {
    delete errors.country
  }
}

const validateForm = () => {
  validateName()
  validateEmail()
  validateSubject()
  validatePhone()
  validateCountry()
  validateServices()
  validateBudget()
  validateMessage()
  return Object.keys(errors).length === 0
}

const submitForm = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  submitStatus.value = 'idle'

  try {
    await sendEmailForm({ ...form, services: [...form.services] })
    submitStatus.value = 'success'

    // Reset du formulaire
    form.name = ''
    form.email = ''
    form.subject = ''
    form.message = ''
    form.budget = ''
    form.services = []

    setTimeout(() => {
      submitStatus.value = 'idle'
    }, 10000)
  } catch (error) {
    submitStatus.value = 'error'
    setTimeout(() => {
      submitStatus.value = 'idle'
    }, 3000)
  } finally {
    isSubmitting.value = false
  }
}

const sendEmailForm = async (formData) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      subject: "Contact Form Webeasy",
      text: `Name : ${formData.name}\nEmail : ${formData.email}\nPhone : ${formData.phone}\nCountry : ${formData.country}\nServices : ${formData.services.join(', ')}\nBudget : ${formData.budget}\nProject detail : ${formData.message}`,
    }),
  };
  const response = await fetch("https://api.webeasy.pro/contact", requestOptions);
  if (response.status === 200) {
    submitStatus.value = 'success';
    setTimeout(() => { submitStatus.value = 'idle'; }, 10000);
  } else {
    submitStatus.value = 'error';
    setTimeout(() => { submitStatus.value = 'idle'; }, 3000);
  }
}

onMounted(() => {
  const offerRaw = localStorage.getItem('selectedOffer');
  if (offerRaw) {
    const offer = JSON.parse(offerRaw);
    // Préremplir le message uniquement avec le nom du pack et le service
    form.message = `Pack choisi : ${offer.offer}\nService : ${offer.service}`;
    // Sélectionner le service correspondant
    const serviceMap = {
      'Backpack': t('contactForm.onePage'),
      '2Pack': t('contactForm.showcase'),
      'Sixpack': t('contactForm.ecommerce'),
      'Jetpack': t('contactForm.custom'),
    };
    const selectedService = serviceMap[offer.offer] || t('contactForm.other');
    if (!form.services.includes(selectedService)) {
      form.services = [selectedService];
    }
    localStorage.removeItem('selectedOffer');
  }
})
</script>

<style scoped>
.contact-container {
  min-height: 100vh;
/*   background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.contact-form-wrapper {
/*   background: rgba(82, 149, 255, 0.04); */
  backdrop-filter: blur(15.54px);
  -webkit-backdrop-filter: blur(15.54px);
  border: 0.65px solid transparent;
  background-image: linear-gradient(rgba(82, 149, 255, 0.04), rgba(82, 149, 255, 0.04)),
                    linear-gradient(-127deg, rgba(82, 149, 255, 0) 0%, rgba(82, 149, 255, 0.2) 100%);
  background-origin: border-box;
/*   background-clip: content-box, border-box; */
  border-radius: 40px;
  padding: 3rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 
    inset 0 0 3.24px 0 rgba(82, 149, 255, 0.24),
    0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.contact-form-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(82, 149, 255, 0.02);
  pointer-events: none;
  border-radius: inherit;
}

.form-title {
  color: #ffffff;
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.form-input {
  width: 100%;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(82, 149, 255, 0.2);
  border-radius: 24px;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-sizing: border-box;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
  font-weight: 400;
}

.form-input:focus {
  outline: none;
  border-color: rgba(82, 149, 255, 0.6);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 0 0 3px rgba(82, 149, 255, 0.1),
    inset 0 0 3.24px 0 rgba(82, 149, 255, 0.24);
  transform: translateY(-1px);
}

.form-input:hover {
  border-color: rgba(82, 149, 255, 0.4);
  background: rgba(255, 255, 255, 0.07);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
}

.form-select {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(82, 149, 255, 0.2);
  border-radius: 24px;
  color: #ffffff;
  font-size: 1rem;
  padding: 1rem 1.25rem;
  width: 100%;
  transition: all 0.3s;
  box-sizing: border-box;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  outline: none;
}

.form-select:focus {
  border-color: rgba(82, 149, 255, 0.6);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(82, 149, 255, 0.1);
}

.form-input.error {
  border-color: rgba(255, 107, 107, 0.6);
  background: rgba(255, 107, 107, 0.05);
}

.error-message {
  color: #ff6b6b;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
  font-weight: 500;
}

.success-message {
  color: #51cf66;
  font-size: 0.875rem;
  margin-top: 1rem;
  text-align: center;
  font-weight: 500;
  padding: 0.75rem;
  background: rgba(81, 207, 102, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(81, 207, 102, 0.2);
}

.submit-button {
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #5295ff 0%, #667eea 100%);
  border: none;
  border-radius: 24px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(82, 149, 255, 0.3);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(82, 149, 255, 0.4);
  background: linear-gradient(135deg, #4285ff 0%, #5a6fd8 100%);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(82, 149, 255, 0.3);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.submit-button.submitting {
  background: linear-gradient(135deg, #6c7ce7 0%, #7c8cf0 100%);
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .contact-container {
    padding: 1rem;
  }
  
  .contact-form-wrapper {
    padding: 2rem 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .form-input {
    padding: 0.875rem 1rem;
  }
}

@media (max-width: 480px) {
  .contact-form-wrapper {
    padding: 1.5rem 1rem;
  }
  
  .form-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .form-input {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
  
  .submit-button {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
}

.services-label {
  color: #fff;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}
.services-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  margin-bottom: 0.25rem;
}
.checkbox-container {
  padding: 2px;
  position: relative;
  padding-left: 28px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  color: #fff;
  user-select: none;
}
.checkbox-container input[type="checkbox"] {
  margin: 100px;
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.checkmark {
  position: absolute;
  left: 0;
  top: 2px;
  height: 20px;
  width: 20px;
  background: rgba(255,255,255,0.08);
  border: 1.5px solid rgba(82, 149, 255, 0.4);
  border-radius: 100%;
  transition: background 0.2s, border-color 0.2s;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}
.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}
.checkbox-container .checkmark:after {
  width: 100%;
  height: 100%;
border-radius: 100%;
background: #00D4FF;
background: radial-gradient(circle, rgba(0, 212, 255, 1) 26%, rgba(9, 9, 121, 1) 95%, rgba(2, 0, 36, 1) 100%);
  transform: rotate(45deg);
  content: "";
  position: absolute;
  background-color: red;
}
@media (max-width: 768px) {
  .services-options {
    flex-direction: column;
    gap: 0.5rem 0;
  }
}
</style>